/**
 * @fileoverview Componente de visualização de promoções para usuários.
 * Exibe grid de cards de promoções seguindo padrão de Category.jsx e Brand.jsx.
 * Admin: vê todas as promoções (ativas + inativas).
 * User: vê apenas promoções ativas.
 * Implementa scroll inteligente para voltar ao contexto.
 * 
 * @module PromotionView
 * @requires react
 * @requires react-redux
 * @requires @mui/material
 * @since 1.0.0
 */

import { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container, Grid, Alert, Typography } from '@mui/material';
import { selectPromotion } from '../../store/slices/productsSlice';
import PromotionCard from './PromotionCard';
import { useLocalData } from '../../hooks/useLocalData';

/**
 * Verifica se usuário é admin.
 * @param {Object} user - Objeto do usuário
 * @returns {boolean} True se admin
 */
const isAdmin = (user) => {
  return user?.roles?.some(role => role.alias === 'admin') || false;
};

/**
 * Componente container para exibição de promoções.
 * Carrega e exibe promoções da API com layout responsivo otimizado.
 * Mobile: 2 cards por linha, Desktop: 4 cards por linha.
 * Implementa scroll automático para centralizar a última promoção visualizada.
 * Admin: vê todas as promoções.
 * Não-admin: vê apenas promoções ativas.
 *
 * @component
 * @returns {JSX.Element} Container responsivo com grid de promoções e navegação contextual
 */
export default function PromotionView() {
  const dispatch = useDispatch();
  const { lastViewedPromotion } = useSelector(state => state.products);
  const { user } = useSelector(state => state.auth);
  const { promotions } = useLocalData(); // Usar dados locais
  const promotionRefs = useRef({});

  const isUserAdmin = useMemo(() => isAdmin(user), [user]);

  // Dados são carregados via contexto global - não precisa de useEffect para fetch

  // Scroll para a última promoção visualizada
  useEffect(() => {
    if (lastViewedPromotion && promotions.length > 0) {
      const promotionElement = promotionRefs.current[lastViewedPromotion.id];
      if (promotionElement) {
        setTimeout(() => {
          promotionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }, 100);
      }
    }
  }, [lastViewedPromotion, promotions]);

  /**
   * Promoções filtradas baseado na role do usuário.
   * Admin: todas as promoções.
   * Não-admin: apenas promoções ativas.
   */
  const filteredPromotions = useMemo(() => {
    if (isUserAdmin) {
      return promotions; // Admin vê todas
    }
    // filter: mantém apenas promoções ativas para usuários não-admin
    return promotions.filter(promo => promo.status === 'active');
  }, [promotions, isUserAdmin]);

  const handlePromotionClick = (promotionData) => {
    try {
      // Busca a promoção completa do array
      const fullPromotion = promotions.find(p => 
        (p.id || p._id) === promotionData.id
      );
      
      if (fullPromotion) {
        console.log('Promoção completa encontrada:', fullPromotion);
        dispatch(selectPromotion(fullPromotion));
      } else {
        console.error('Promoção não encontrada no array');
      }
    } catch (error) {
      console.error('Erro ao selecionar promoção:', error);
    }
  };

  // Loading e erro são gerenciados pelo AppInitializer via contexto global

  return (
    <Box component='main' sx={{ pt: 8, pb: 6, minHeight: '100vh' }}>
      <Container
        maxWidth='lg'
        sx={{
          py: 4,
          mt: { xs: 5, sm: 5, md: '100px' },
          px: { xs: 1, sm: 3, md: 3 },
        }}
      >
        <Typography
          variant='h5'
          component='h5'
          sx={{
            mt: { xs: 8, sm: 8, md: 8 },
            textAlign: 'left',
            fontWeight: 'bold',
          }}
        >
          Promoções
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid
            container
            spacing={{ xs: 1, sm: 3, md: 3 }}
            sx={{
              maxWidth: { xs: '100%', sm: 1000, md: 1200 },
              width: '100%',
              margin: 0,
              mt: { xs: 1, md: 1 },
            }}
          >
            {filteredPromotions.length > 0 ? (
              filteredPromotions.map(promotion => (
                <Grid
                  key={promotion.id || promotion._id}
                  size={{ xs: 6, sm: 6, md: 3, lg: 3 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: 0,
                    maxWidth: '100%',
                  }}
                >
                  <div
                    ref={el => promotionRefs.current[promotion.id || promotion._id] = el}
                    style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                  >
                    <PromotionCard
                      id={promotion.id || promotion._id}
                      name={promotion.name}
                      description={promotion.context?.description}
                      image_url={promotion.context?.image_url}
                      status={promotion.status}
                      isAdmin={isUserAdmin}
                      onClick={handlePromotionClick}
                    />
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        mt: 1,
                        textAlign: 'center',
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {promotion.name}
                    </Typography>
                  </div>
                </Grid>
              ))
            ) : (
              <Grid size={12}>
                <Alert severity='info'>Nenhuma promoção encontrada</Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
